/**
 * CenoX Business → Logistics.
 *
 * Seven screens behind one tab strip. Tabs rather than a dropdown for the same
 * reason the reports page uses them: moving between Settlements and Deliveries
 * is something a dispatcher does constantly, and a select box makes every trip
 * four clicks instead of one.
 *
 * The strip scrolls horizontally on a phone rather than wrapping into two rows,
 * so the content starts at the same place on every screen width.
 */

import type { ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { cn } from '../../components/ui';
import { ScreenError } from '../../components/ScreenError';

const TABS = [
  { to: '/business/logistics', label: 'Overview', end: true },
  { to: '/business/logistics/deliveries', label: 'Deliveries' },
  { to: '/business/logistics/riders', label: 'Riders' },
  { to: '/business/logistics/settlements', label: 'Settlements' },
  { to: '/business/logistics/reports', label: 'Reports' },
  { to: '/business/logistics/returns', label: 'Returns' },
  { to: '/business/logistics/import', label: 'Import' },
] as const;

export function LogisticsLayout(): ReactNode {
  const location = useLocation();
  const current = TABS.find((t) => t.to === location.pathname)?.label ?? 'Logistics';

  return (
    <>
      <header className="mb-4">
        <h1 className="text-xl font-semibold text-slate-900">Logistics</h1>
        <p className="mt-0.5 text-sm text-slate-500">
          Deliveries, riders and what each one is owed.
        </p>
      </header>

      <nav
        className="mb-5 flex gap-1 overflow-x-auto border-b border-slate-200 pb-px"
        aria-label="Logistics section"
      >
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={'end' in tab ? tab.end : false}
            className={({ isActive }) =>
              cn(
                'whitespace-nowrap rounded-t-lg px-3.5 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'border-b-2 border-brand-600 text-brand-700'
                  : 'border-b-2 border-transparent text-slate-500 hover:text-slate-800',
              )
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      {/*
        Keyed on the path so moving to another tab clears a failure rather than
        carrying it: the boundary holds its error until it is remounted, and a
        broken Riders screen must not make Deliveries look broken too.
      */}
      <ScreenError key={location.pathname} screen={current}>
        <Outlet />
      </ScreenError>
    </>
  );
}
